import { skipToken } from '@apollo/client'
import { format, subMonths } from 'date-fns'
import { ArrowRight, BarChart3 } from 'lucide-react'
import { useMemo } from 'react'
import { Link } from 'react-router'
import { H3, Muted } from '@/components/sv/typographies'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import {
  useCompanyPath,
  useIdCompany,
  useRBAC,
  useSuspenseQuery,
} from '@/lib/hooks'
import { BILLING_PERIOD_QUERY } from '@/routes/home/spending-overview/query'
import { EmptySummaryCards } from '@/routes/usage/components/empty-summary-cards'
import { EmptyUsageChart } from '@/routes/usage/components/empty-usage-chart'
import { SummaryCards } from '@/routes/usage/components/summary-cards'
import { UsageChart } from '@/routes/usage/components/usage-chart'
import { BILLING_DAILY_USAGE_REPORT_QUERY } from '@/routes/usage/queries'
import { computeProjection } from '@/routes/usage/utils'
import type {
  DashboardBillingPeriodQuery,
  DashboardBillingPeriodQueryVariables,
} from '@/routes/home/spending-overview/query.types.gen'
import type {
  BillingDailyUsageReportQuery,
  BillingDailyUsageReportQueryVariables,
} from '@/routes/usage/queries.types.gen'

const toDate = (value: Date | string | number | null | undefined) => {
  if (value === null || value === undefined) {
    return null
  }
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export const SpendingOverviewChartCard = () => {
  const { idCompany } = useIdCompany()
  const { rbac } = useRBAC()
  const { getCompanyPath } = useCompanyPath()
  const canViewUsage = rbac.company(idCompany).usage.view.can()

  const { data: periodData } = useSuspenseQuery<
    DashboardBillingPeriodQuery,
    DashboardBillingPeriodQueryVariables
  >(BILLING_PERIOD_QUERY, {
    variables: { idCompany },
  })

  const subscription = periodData?.company?.sevallaBilling?.subscription
  const currentPeriodStart = toDate(subscription?.currentPeriodStart)
  const currentPeriodEnd = toDate(subscription?.currentPeriodEnd)

  const previousPeriodStart = useMemo(
    () => (currentPeriodStart ? subMonths(currentPeriodStart, 1) : null),
    [currentPeriodStart]
  )
  const previousPeriodEnd = useMemo(
    () => (currentPeriodEnd ? subMonths(currentPeriodEnd, 1) : null),
    [currentPeriodEnd]
  )

  const { data: currentDailyData } = useSuspenseQuery<
    BillingDailyUsageReportQuery,
    BillingDailyUsageReportQueryVariables
  >(
    BILLING_DAILY_USAGE_REPORT_QUERY,
    !currentPeriodStart || !currentPeriodEnd
      ? skipToken
      : {
          variables: {
            idCompany,
            startDate: currentPeriodStart,
            endDate: currentPeriodEnd,
            hasPermission: canViewUsage,
          },
        }
  )

  const { data: previousDailyData } = useSuspenseQuery<
    BillingDailyUsageReportQuery,
    BillingDailyUsageReportQueryVariables
  >(
    BILLING_DAILY_USAGE_REPORT_QUERY,
    !previousPeriodStart || !previousPeriodEnd
      ? skipToken
      : {
          variables: {
            idCompany,
            startDate: previousPeriodStart,
            endDate: previousPeriodEnd,
            hasPermission: canViewUsage,
          },
        }
  )

  const currentReport =
    currentDailyData?.company?.dailyResourceUsageReport ?? null
  const previousReport =
    previousDailyData?.company?.dailyResourceUsageReport ?? null

  const currentTotal = currentReport?.totalGross ?? 0
  const previousTotal = previousReport?.totalGross ?? 0

  const projection = useMemo(() => {
    if (!currentReport || !currentPeriodEnd) {
      return { projectedTotal: 0, projectedDailyAmount: 0 }
    }

    return computeProjection({
      days: currentReport.days,
      periodEnd: currentPeriodEnd,
    })
  }, [currentReport, currentPeriodEnd])

  const hasUsageData =
    currentReport !== null &&
    currentReport.days.some((day) =>
      day.services.some((svc) => svc.usageByType.length > 0)
    )

  return (
    <Card className="bg-secondary p-0 gap-0">
      <CardHeader className="px-5 py-3 flex flex-row items-center justify-between gap-0">
        <CardTitle>
          <H3 className="flex items-center gap-2 text-sm text-muted-foreground">
            <BarChart3 className="size-4" /> Spending overview
          </H3>
        </CardTitle>
        <div className="flex items-center gap-4">
          {currentPeriodStart && currentPeriodEnd && (
            <Muted className="text-sm tabular-nums">
              {format(currentPeriodStart, 'MMM dd, yyyy')} &ndash;{' '}
              {format(currentPeriodEnd, 'MMM dd, yyyy')}
            </Muted>
          )}
          <Link
            to={getCompanyPath({ pathname: '/usage' })}
            className="inline-flex items-center gap-1 text-sm text-primary underline-offset-4 hover:underline"
          >
            Detailed usage
            <ArrowRight className="size-3.5" />
          </Link>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Card className="p-0 border-x-0 border-b-0 shadow-none">
          <CardContent className="p-0">
            {hasUsageData ? (
              <>
                <SummaryCards
                  currentTotal={currentTotal}
                  projectedTotal={projection.projectedTotal}
                  previousTotal={previousTotal}
                  isPreviousPeriod={false}
                  previousPeriodStart={previousPeriodStart}
                />
                <Separator />
                <div className="py-6">
                  <UsageChart
                    days={currentReport?.days ?? []}
                    projectedDailyAmount={projection.projectedDailyAmount}
                    periodStart={currentPeriodStart}
                    periodEnd={currentPeriodEnd}
                    highlightedType={null}
                    showControls={false}
                  />
                </div>
              </>
            ) : (
              <>
                <EmptySummaryCards />
                <Separator />
                <EmptyUsageChart />
              </>
            )}
          </CardContent>
        </Card>
      </CardContent>
    </Card>
  )
}
